import { Button, Card, CardContent, CircularProgress, Grid, Icon, Tooltip, Typography } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Alert from 'components/Alert';
import PermissionHide from 'components/PermissionHide';
import { variables } from 'components/Theme';
import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE } from 'helpers/constants';
import { userHasNoDocumentAlert } from 'helpers/functions';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import ConfirmTransactionMessage from 'v2/components/Shared/ConfirmTransactionMessage';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';

import type IProfile from 'v2/interfaces/profile';

import { FidcAlertModal } from '../FidcAlertModal';
import ModalFiscal from '../ModalFiscal';

const MIN_VALUE_TO_TRANSFER_DOLAR = 1000;
//const MIN_VALUE_TO_TRANSFER_REAL = 5500;

const useStyle = makeStyles((theme) => ({
  wallet: {
    boxShadow: variables.shadows[1],
    color: '#586375',
  },
  title: {
    fontSize: 17.5,
  },
  subTitle: {
    fontSize: 11,
  },
  valueContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  transferButton: {
    background: '#009358',
    color: 'white',
    float: 'right',
    [theme.breakpoints.down('sm')]: {
      float: 'none',
    },
    '&:hover': {
      background: '#007A49',
    },
    '&:disabled': {
      background: '#eee',
    },
  },
}));

const CrossBorder = () => {
  const classes = useStyle({});

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const currency = 'USD';
  const MinAbleToTransfer = MIN_VALUE_TO_TRANSFER_DOLAR;

  const [loading, setLoading] = useState(false);
  const [visibleFiscal, setVisibleFiscal] = useState(false);
  const [profile, setProfile] = useState<IProfile>(null);
  const [showFidcAlertModal, setShowFidcAlertModal] = useState(false);

  const [data, error] = useObservable(() => financialService.listAvailable().pipe(), []);
  const [bankAccounts, bankAccountsError] = useObservable(() => financialService.listBankAccounts({ active: 'true' }).pipe(), []);

  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');

  const handleCloseModalFiscal = useCallback(() => {
    setVisibleFiscal(false);
  }, []);

  const totalToTransfer = useMemo(() => {
    return data?.origin_value || 0;
  }, [data]);

  const disableTransferButton = useMemo(() => {
    if (totalToTransfer < MinAbleToTransfer) {
      return true;
    }

    if (userHasBlockTransferBetaModule) {
      return true;
    }

    return false;
  }, [MinAbleToTransfer, totalToTransfer, userHasBlockTransferBetaModule]);

  const bankSelected = useMemo(() => (bankAccounts?.length ? bankAccounts[0] : null), [bankAccounts]);

  const isLoading = data === null && error === undefined;
  const isLoadingBankAccounts = bankAccounts === null && bankAccountsError === undefined;

  const [sendTransfer] = useCallbackObservable(() => {
    setLoading(true);
    return financialService.sendTransfer({ invoices: [], transfer_method_id: bankSelected.id, value: totalToTransfer }).pipe(
      tap(
        () => {
          toast.success('Sucesso, transferência enviada para processamento com êxito!');
          setLoading(false);
        },
        (err) => {
          setLoading(false);
          toast.error(err);
        }
      )
    );
  }, [totalToTransfer, bankSelected]);

  const makeTransfer = useCallback(async () => {
    setLoading(true);

    const { profile, hasDocument } = await userHasNoDocumentAlert();

    if (!hasDocument) {
      setLoading(false);
      setProfile(profile);
      setVisibleFiscal(true);
      return;
    }

    setLoading(false);

    const confirm = await Alert.confirm({
      message: <ConfirmTransactionMessage value={totalToTransfer} bankAccount={bankSelected} />,
      title: 'Atenção',
      buttonOk: 'Confirmar',
      confirmation: true,
    });

    if (confirm) {
      sendTransfer();
    }
  }, [bankSelected, sendTransfer, totalToTransfer]);

  const handleSubmit = useCallback(async () => {
    const showGalaxyEligibleAlert = user?.eligible;
    if (showGalaxyEligibleAlert) {
      setShowFidcAlertModal(true);
      return;
    }

    makeTransfer();
  }, [user?.eligible, makeTransfer]);

  const handleCloseFidcAlertModal = () => {
    setShowFidcAlertModal(false);
    makeTransfer();
  };

  const renderTooltipValue = useMemo(() => {
    if (userHasBlockTransferBetaModule) {
      return FINANCIAL_BLOCK_TOOLTIP_MESSAGE;
    }

    if (totalToTransfer < MinAbleToTransfer) {
      return (
        <p>
          Valor mínimo para transferir: <FormattedNumber value={MinAbleToTransfer} format={currency}></FormattedNumber>
        </p>
      );
    }

    return '';
  }, [MinAbleToTransfer, currency, totalToTransfer, userHasBlockTransferBetaModule]);

  const renderTransferButton = useMemo(() => {
    if (isLoadingBankAccounts) return <div />;

    if (!bankSelected) {
      return <div>Você precisa de uma conta bancária cadastrada para transferir</div>;
    }

    if (user?.has_transfer) {
      return <div>Você tem transferências em processamento</div>;
    }

    if (bankAccountsError) {
      return <div>Erro ao buscar contas bancárias</div>;
    }

    return (
      <div className={classes.buttonContainer}>
        <Tooltip title={renderTooltipValue} placement='top'>
          <div>
            <Button className={classes.transferButton} disabled={loading || disableTransferButton} onClick={handleSubmit} id='myTransferButton'>
              <Icon style={{ marginRight: '0.5rem', fontSize: 14 }}>attach_money</Icon>Transferir agora
            </Button>
          </div>
        </Tooltip>
      </div>
    );
  }, [
    bankAccountsError,
    bankSelected,
    classes.buttonContainer,
    classes.transferButton,
    disableTransferButton,
    handleSubmit,
    isLoadingBankAccounts,
    loading,
    renderTooltipValue,
    user,
  ]);

  if (!user?.crossborder) return null;

  return (
    <div id='transfer-card-pj'>
      <Card elevation={0} className={classes.wallet}>
        <CardContent>
          <Grid container>
            <Grid item xs={12} sm={3}>
              <Typography color='inherit' component='h2' variant='h6'>
                Carteira
              </Typography>
              <Typography color='inherit' gutterBottom variant='subtitle1' component='span' style={{ margin: 0, fontSize: 12 }}>
                Cross Border
              </Typography>
            </Grid>

            <Grid item xs={12} sm={9} id='transfer-card-pj-values'>
              <div>
                <Typography align='right' color='inherit' component='h2' className={classes.title}>
                  <b>Disponível para Transferência:</b>
                </Typography>

                <Typography align='right' color='inherit' className={classes.subTitle}>
                  Valor total liberado para ser transferido
                </Typography>
              </div>

              <div className={classes.valueContainer}>
                <Typography align='right' color='inherit' component='h2' variant='h5'>
                  {!isLoading ? <FormattedNumber format={currency} value={data?.origin_value || 0} /> : <CircularProgress size={20} />}
                </Typography>
              </div>
            </Grid>
          </Grid>

          <div className='m8' />
          <PermissionHide action={'myeduzz:financeiro:solicitar-transferencia'}>{renderTransferButton}</PermissionHide>
        </CardContent>
      </Card>

      <ModalFiscal opened={visibleFiscal} onClose={handleCloseModalFiscal} onComplete={handleCloseModalFiscal} profile={profile} />
      <FidcAlertModal isOpen={showFidcAlertModal} onClose={handleCloseFidcAlertModal} />
    </div>
  );
};

export default CrossBorder;
